import { apiSlice } from "./apiSlice";
import {
  LessonEntity,
  PaginatedLessonsResponse,
} from "../../services/lessons/lessons.types";

export interface GetLessonsPaginatedArgs {
  page: number;
  pageSize: number;
  lessonId?: number;
  lessonTitle?: string;
  courseTitle?: string;
}

export interface LessonsPage {
  data: LessonEntity[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const lessonsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLessonsPaginated: builder.query<LessonsPage, GetLessonsPaginatedArgs>({
      query: ({ page, pageSize, lessonId, lessonTitle, courseTitle }) => ({
        url: "/lessons",
        method: "GET",
        params: {
          page,
          pageSize,
          ...(lessonId !== undefined && { lessonId }),
          ...(lessonTitle && { lessonTitle }),
          ...(courseTitle && { courseTitle }),
        },
      }),
      transformResponse: (response: PaginatedLessonsResponse): LessonsPage => ({
        data: response.data,
        pagination: {
          page: response.page,
          pageSize: response.pageSize,
          total: response.total,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Lesson" as const, id })),
              { type: "Lesson" as const, id: "LIST" },
            ]
          : [{ type: "Lesson" as const, id: "LIST" }],
    }),
    // Inline rename from the list. The backend PATCH is a real partial update
    // (UpdateLessonDto = PartialType(CreateLessonDto)) and its service takes
    // an early scalar-only path when `activities` is absent, so sending only
    // the title can never touch the lesson's activities.
    updateLessonTitle: builder.mutation<
      Pick<LessonEntity, "id" | "title">,
      { id: number; title: string }
    >({
      query: ({ id, title }) => ({
        url: `/lessons/${id}`,
        method: "PATCH",
        data: { title },
      }),
      // The rename also lands in the audit trail, so the Actividad cache
      // refreshes too. Error guard: a failed rename must not refetch anything.
      invalidatesTags: (result, error, { id }) =>
        error
          ? []
          : [
              { type: "Lesson" as const, id },
              { type: "Lesson" as const, id: "LIST" },
              { type: "AuditLog" as const, id: "LIST" },
            ],
    }),
    deleteLesson: builder.mutation<void, number>({
      query: (id) => ({
        url: `/lessons/${id}`,
        method: "DELETE",
      }),
      // Deleted lessons land in the trash, so its cache must refresh too.
      // Error guard: a failed delete must not refetch anything.
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "Lesson" as const, id: "LIST" }, "Trash" as const],
    }),
    // children_mode 'duplicate': the new lesson gets independent copies of
    // its activities; 'link': the copy shares the SAME activities. The copy
    // is standalone (not linked to any course) in both modes.
    duplicateLesson: builder.mutation<
      { id: number; title: string },
      { id: number; title: string; children_mode: "duplicate" | "link" }
    >({
      query: ({ id, title, children_mode }) => ({
        url: `/lessons/${id}/duplicate`,
        method: "POST",
        data: { title, children_mode },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "Lesson" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetLessonsPaginatedQuery,
  useUpdateLessonTitleMutation,
  useDeleteLessonMutation,
  useDuplicateLessonMutation,
} = lessonsApi;
